
/**
 * Logging utility that provides environment-aware logging functions
 * Logs are enabled in development but disabled in production
 */

// Determine the current environment
const isDevelopment = import.meta.env.MODE === 'development';
const isTest = import.meta.env.MODE === 'test';
const isProduction = !isDevelopment && !isTest;

// Check for override environment variables
const LOG_LEVEL = import.meta.env.VITE_LOG_LEVEL || 'info';

// Log levels with numeric values for comparison
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

// Get the numeric log level based on the environment or override
const getLogLevelValue = () => {
  // In production, default to 'error' level unless overridden
  if (isProduction && !import.meta.env.VITE_LOG_LEVEL) {
    return LOG_LEVELS.error;
  }
  
  return LOG_LEVELS[LOG_LEVEL as keyof typeof LOG_LEVELS] || LOG_LEVELS.info;
};

// The current log level value
const currentLogLevel = getLogLevelValue();

// Whether a particular log level should be displayed
const shouldLog = (level: keyof typeof LOG_LEVELS): boolean => {
  return LOG_LEVELS[level] >= currentLogLevel;
};

// Format the log messages with additional context
const formatMessage = (
  message: string | Error | unknown,
  context?: Record<string, unknown>
): [string, ...unknown[]] => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}]`;
  
  // Handle different message types
  if (message instanceof Error) {
    return [
      `${prefix} %c${message.name}%c: ${message.message}`, 
      'color: red; font-weight: bold', 
      'color: inherit',
      ...(context ? [context] : []),
      message,
    ];
  }
  
  if (typeof message === 'string') {
    return [
      `${prefix} ${message}`,
      ...(context ? [context] : []),
    ];
  }
  
  return [
    `${prefix} %o`,
    message,
    ...(context ? [context] : []),
  ];
};

/**
 * Debug level logging - only for detailed debugging information
 * Only appears in development by default
 */
export const debug = (
  message: string | Error | unknown,
  context?: Record<string, unknown>
) => {
  if (shouldLog('debug')) {
    console.debug(...formatMessage(message, context));
  }
};

/**
 * Info level logging - for general information about application operation
 * Only appears in development by default
 */
export const info = (
  message: string | Error | unknown,
  context?: Record<string, unknown>
) => {
  if (shouldLog('info')) {
    console.info(...formatMessage(message, context));
  }
};

/**
 * Warning level logging - for issues that are not errors but might require attention
 * Appears in development and test environments by default
 */
export const warn = (
  message: string | Error | unknown,
  context?: Record<string, unknown>
) => {
  if (shouldLog('warn')) {
    console.warn(...formatMessage(message, context));
  }
};

/**
 * Error level logging - for errors and exceptions
 * Always appears in all environments unless explicitly disabled
 */
export const error = (
  message: string | Error | unknown,
  context?: Record<string, unknown>
) => {
  if (shouldLog('error')) {
    console.error(...formatMessage(message, context));
  }
};

/**
 * Group logs related to a specific operation
 * Only appears based on the current log level
 */
export const group = (
  label: string,
  logLevel: keyof typeof LOG_LEVELS = 'debug',
  collapsed: boolean = true
) => {
  if (shouldLog(logLevel)) {
    if (collapsed) {
      console.groupCollapsed(label);
    } else {
      console.group(label);
    }
  }
};

/**
 * End a logging group
 * Only appears based on the current log level
 */
export const groupEnd = (logLevel: keyof typeof LOG_LEVELS = 'debug') => {
  if (shouldLog(logLevel)) {
    console.groupEnd();
  }
};

/**
 * Create a logger instance with a specific context
 * Useful for component or module-specific logging
 */
export const createLogger = (moduleName: string) => {
  const moduleContext = { module: moduleName };
  
  return {
    debug: (message: string | Error | unknown, context?: Record<string, unknown>) => 
      debug(message, { ...moduleContext, ...context }),
    info: (message: string | Error | unknown, context?: Record<string, unknown>) => 
      info(message, { ...moduleContext, ...context }),
    warn: (message: string | Error | unknown, context?: Record<string, unknown>) => 
      warn(message, { ...moduleContext, ...context }),
    error: (message: string | Error | unknown, context?: Record<string, unknown>) => 
      error(message, { ...moduleContext, ...context }),
    group: (label: string, logLevel: keyof typeof LOG_LEVELS = 'debug', collapsed: boolean = true) => 
      group(`${moduleName}: ${label}`, logLevel, collapsed),
    groupEnd: (logLevel: keyof typeof LOG_LEVELS = 'debug') => 
      groupEnd(logLevel),
  };
};

// Default export of the entire logger
const logger = {
  debug,
  info,
  warn,
  error,
  group,
  groupEnd,
  createLogger,
};

export default logger;
